import { Optional } from '../utility';

import {
    TransportOrderMode,
    TransportOrderRole,
    TransportStatus,
    TransportType
} from '../../constants/transports';

export interface TransportPackageInterface {
    ID: number;
    ID_TRANSPORT: number;
    CREATED_AT: string | Date | null;
    SHIPPING_CREATED_AT: string | Date | null;
    ON_THE_WAY_AT: string | Date | null;
    DELIVERED_AT: string | Date | null;
    RETURNING_BACK_AT: string | Date | null;
    RETURNED_BACK_AT: string | Date | null;
    CANCELED_AT: string | Date | null;
    INCORRECT_AT: string | Date | null;
    LOST_AT: string | Date | null;
    STATUS: TransportStatus | string;
    WIDTH: number | null;
    HEIGHT: number | null;
    LENGTH: number | null;
    WEIGHT: number | null;
    PRICE: number | null;
    CURRENCY: string | null;
    EXPEDITION_BOX_NUMBER: number | null;
    INCORRECT_MESSAGE: string | null;
    LINE_STATE: string | null;
    PACKAGE_TYPE?: string | null;
    VOLUME?: number | null;
    LOADING_METERS?: number | null;
    ID_ORDER_EXPEDITION_PACKAGE?: number | null;
    ID_ORDER_PALLET?: number | null;
    WAREHOUSE_TRACKING_NUMBER?: string | null;
}

export interface TransportInterface {
    ID: number;
    ESHOP_KEY: string;
    ESHOP_TRANSPORT_ID: string;
    CREATED_AT: string | Date | null;
    SHIPPING_CREATED_AT: string | Date | null;
    ON_THE_WAY_AT: string | Date | null;
    AT_PICKUP_POINT_AT: string | Date | null;
    DELIVERED_AT: string | Date | null;
    RETURNING_BACK_AT: string | Date | null;
    RETURNED_BACK_AT: string | Date | null;
    CANCELED_AT: string | Date | null;
    INCORRECT_AT: string | Date | null;
    LOST_AT: string | Date | null;
    UPDATED_AT: string | Date | null;
    EXECUTE_AT: string | Date | null;
    PICK_UP_AT: string | Date | null;
    LAST_STATUS_CHECK_AT: string | Date | null;
    ID_CREATOR: number | null;
    TYPE: TransportType | string | null;
    STATUS: TransportStatus | string;
    ORDER_AFTER?: string | Date | null;
    ORDER_CLAIMED_AT?: string | Date | null;
    ORDER_MODE?: TransportOrderMode | string | null;
    RECIPIENT_NAME: string | null;
    RECIPIENT_ATTENTION_NAME: string | null;
    RECIPIENT_EMAIL: string | null;
    RECIPIENT_PHONE: string | null;
    RECIPIENT_COMPANY_NAME: string | null;
    RECIPIENT_COMPANY_ID: string | null;
    RECIPIENT_TAX_ID: string | null;
    RECIPIENT_STREET: string | null;
    RECIPIENT_ADDRESS_ADDITIONAL_INFO: string | null;
    RECIPIENT_ADDRESS_ADDITIONAL_INFO_2: string | null;
    RECIPIENT_CITY: string | null;
    RECIPIENT_STATE: string | null;
    RECIPIENT_PROVINCE_CODE: string | null;
    RECIPIENT_POST_CODE: string | null;
    SENDER_NAME: string | null;
    SENDER_ATTENTION_NAME: string | null;
    SENDER_EMAIL: string | null;
    SENDER_PHONE: string | null;
    SENDER_COMPANY_NAME: string | null;
    SENDER_COMPANY_ID: string | null;
    SENDER_TAX_ID: string | null;
    SENDER_STREET: string | null;
    SENDER_ADDRESS_ADDITIONAL_INFO: string | null;
    SENDER_ADDRESS_ADDITIONAL_INFO_2: string | null;
    SENDER_CITY: string | null;
    SENDER_STATE: string | null;
    SENDER_PROVINCE_CODE: string | null;
    SENDER_POST_CODE: string | null;
    SHIPPER: string | null;
    WEIGHT: number | null;
    UNIT: string | null;
    PRICE: number | null;
    SHIPPING_PRICE: number | null;
    CURRENCY: string | null;
    SHIPPING_CURRENCY: string | null;
    PAID: number | null;
    PAYMENT_METHOD: string | null;
    SHIPPER_SERVICE_CODE: string | null;
    COD_PRICE: number | null;
    COD_CURRENCY: string | null;
    COD_REF_NUMBER: string | null;
    MESSAGE: string | null;
    INVOICE_NUMBER: string | null;
    ADDRESS_ID: string | null;
    INCORRECT_MESSAGE: string | null;
    ORDER_BOX_COUNT: number | null;
    SHIPMENT_PENDING: number | null;
    TRANSPORT_PAID_BY_SKLADON: number | null;
    AGECHECK: number | null;
    NEXT_STATUS_CHECK_AT?: string | Date | null;
    SUPPLIER?: string | null;
    BILLED_WEIGHT?: number | null;
    SHIPPER_SHIPMENT_ID?: string | null;
    TRACKING_NUMBER?: string | null;
    MARKETPLACE?: number | null;
    /** Employee-only (EGON.VIEW). NULL = direct carrier, 'egon' = ordered via aggregator. Never exposed to customers. */
    CARRIER_BACKEND?: string | null;
    /** Employee-only. Snapshot stamped by core. */
    EGON_DELIVERY_ID?: number | null;
    /** Employee-only. Snapshot stamped by core. */
    EGON_TRANSFER_TYPE?: number | null;
    /** Employee-only. Owned by transport-ms, read-only for core. */
    EGON_ORDER_ID?: number | null;
    /** Employee-only. Owned by transport-ms, read-only for core. */
    EGON_ORIGINAL_ORDER_ID?: string | null;
    /** Employee-only. Owned by transport-ms, read-only for core. */
    EGON_ORDER_SEQ?: number | null;
    /** Employee-only. Owned by transport-ms, read-only for core. */
    EGON_STATUS_ID?: number | null;
    /** Employee-only. Owned by transport-ms, read-only for core. */
    EGON_STATUS_NAME?: string | null;
    /** Employee-only. Owned by transport-ms, read-only for core. */
    EGON_STATUS_AT?: string | Date | null;
    /** Employee-only. Owned by transport-ms, read-only for core. */
    EGON_ORDERED_AT?: string | Date | null;
}

export const EGON_CARRIER_BACKEND = 'egon';

/** Transport columns visible only with EGON.VIEW. */
export const EGON_TRANSPORT_FIELDS = [
    'CARRIER_BACKEND',
    'EGON_DELIVERY_ID',
    'EGON_TRANSFER_TYPE',
    'EGON_ORDER_ID',
    'EGON_ORIGINAL_ORDER_ID',
    'EGON_ORDER_SEQ',
    'EGON_STATUS_ID',
    'EGON_STATUS_NAME',
    'EGON_STATUS_AT',
    'EGON_ORDERED_AT'
] as const;

export type EgonTransportField = (typeof EGON_TRANSPORT_FIELDS)[number];

export const EGON_ALLOWED_TRANSFER_TYPES = [1, 2, 4, 18] as const;

export interface EgonRouteInterface {
    ID: number;
    ID_SERVICE_CODE: number;
    COUNTRY_CODE: string;
    EGON_DELIVERY_ID: number;
    EGON_DELIVERY_NAME: string | null;
    EGON_TRANSFER_TYPE: number | null;
    ENABLED: number;
    NOTE: string | null;
    CREATED_AT?: string | Date | null;
    UPDATED_AT?: string | Date | null;
    UPDATED_BY?: string | null;
    SERVICE_CODE_VALUE?: string;
    SERVICE_CODE_LABEL?: string;
    SHIPPER_CODE?: string;
}

export interface EgonDeliveryInterface {
    id: number;
    name: string;
    transferType: number | null;
    transferTypeName: string | null;
    codAvailability: boolean;
}

export interface TransportItemInterface {
    ID: number;
    ID_TRANSPORT: number;
    SKU: string | null;
    NAME: string | null;
    DESCRIPTION: string | null;
    COUNT: number;
    UNIT_PRICE: number | null;
    CURRENCY: string | null;
    WEIGHT: number | null;
    HS_CODE: string | null;
    ORIGIN_COUNTRY: string | null;
}

export type ReceivedTransportItem = Omit<TransportItemInterface, 'ID' | 'ID_TRANSPORT'>;

export interface TransportWithPackagesInterface extends TransportInterface {
    PACKAGES: TransportPackageInterface[];
    /** Persisted items carry ID/ID_TRANSPORT; items received for saving do not. */
    ITEMS?: Array<TransportItemInterface | ReceivedTransportItem>;
}

export type ReceivedTransportPackage = Optional<
    TransportPackageInterface,
    | 'ID'
    | 'ID_TRANSPORT'
    | 'CREATED_AT'
    | 'SHIPPING_CREATED_AT'
    | 'ON_THE_WAY_AT'
    | 'DELIVERED_AT'
    | 'RETURNING_BACK_AT'
    | 'RETURNED_BACK_AT'
    | 'CANCELED_AT'
    | 'INCORRECT_AT'
    | 'LOST_AT'
    | 'STATUS'
    | 'WIDTH'
    | 'HEIGHT'
    | 'LENGTH'
    | 'WEIGHT'
    | 'PRICE'
    | 'CURRENCY'
    | 'EXPEDITION_BOX_NUMBER'
    | 'INCORRECT_MESSAGE'
    | 'LINE_STATE'
>;

export type ReceivedTransport = Optional<
    TransportInterface,
    | 'ID'
    | 'ESHOP_KEY'
    | 'ESHOP_TRANSPORT_ID'
    | 'CREATED_AT'
    | 'SHIPPING_CREATED_AT'
    | 'ON_THE_WAY_AT'
    | 'AT_PICKUP_POINT_AT'
    | 'DELIVERED_AT'
    | 'RETURNING_BACK_AT'
    | 'RETURNED_BACK_AT'
    | 'CANCELED_AT'
    | 'INCORRECT_AT'
    | 'LOST_AT'
    | 'UPDATED_AT'
    | 'EXECUTE_AT'
    | 'PICK_UP_AT'
    | 'LAST_STATUS_CHECK_AT'
    | 'ID_CREATOR'
    | 'TYPE'
    | 'STATUS'
    | 'ORDER_AFTER'
    | 'ORDER_CLAIMED_AT'
    | 'ORDER_MODE'
    | 'RECIPIENT_NAME'
    | 'RECIPIENT_ATTENTION_NAME'
    | 'RECIPIENT_EMAIL'
    | 'RECIPIENT_PHONE'
    | 'RECIPIENT_COMPANY_NAME'
    | 'RECIPIENT_COMPANY_ID'
    | 'RECIPIENT_TAX_ID'
    | 'RECIPIENT_STREET'
    | 'RECIPIENT_ADDRESS_ADDITIONAL_INFO'
    | 'RECIPIENT_ADDRESS_ADDITIONAL_INFO_2'
    | 'RECIPIENT_CITY'
    | 'RECIPIENT_STATE'
    | 'RECIPIENT_PROVINCE_CODE'
    | 'RECIPIENT_POST_CODE'
    | 'SENDER_NAME'
    | 'SENDER_ATTENTION_NAME'
    | 'SENDER_EMAIL'
    | 'SENDER_PHONE'
    | 'SENDER_COMPANY_NAME'
    | 'SENDER_COMPANY_ID'
    | 'SENDER_TAX_ID'
    | 'SENDER_STREET'
    | 'SENDER_ADDRESS_ADDITIONAL_INFO'
    | 'SENDER_ADDRESS_ADDITIONAL_INFO_2'
    | 'SENDER_CITY'
    | 'SENDER_STATE'
    | 'SENDER_PROVINCE_CODE'
    | 'SENDER_POST_CODE'
    | 'SHIPPER'
    | 'WEIGHT'
    | 'UNIT'
    | 'PRICE'
    | 'SHIPPING_PRICE'
    | 'CURRENCY'
    | 'SHIPPING_CURRENCY'
    | 'PAID'
    | 'PAYMENT_METHOD'
    | 'SHIPPER_SERVICE_CODE'
    | 'COD_PRICE'
    | 'COD_CURRENCY'
    | 'COD_REF_NUMBER'
    | 'MESSAGE'
    | 'INVOICE_NUMBER'
    | 'ADDRESS_ID'
    | 'INCORRECT_MESSAGE'
    | 'ORDER_BOX_COUNT'
    | 'SHIPMENT_PENDING'
    | 'TRANSPORT_PAID_BY_SKLADON'
    | 'AGECHECK'
    | 'TRACKING_NUMBER'
    | 'MARKETPLACE'
> & {
    PACKAGES?: ReceivedTransportPackage[];
    ITEMS?: ReceivedTransportItem[];
};

export type TransportResponse = TransportWithPackagesInterface;

export type TransportListItem = Pick<
    TransportInterface,
    | 'ID'
    | 'ESHOP_TRANSPORT_ID'
    | 'CREATED_AT'
    | 'STATUS'
    | 'TYPE'
    | 'RECIPIENT_NAME'
    | 'SENDER_NAME'
    | 'SHIPPER'
    | 'SHIPPER_SERVICE_CODE'
>;

export interface TransportOrderInterface {
    ID_TRANSPORT: number;
    ID_ORDER: number;
    ROLE: TransportOrderRole;
    ACTIVE_SYSTEM: number | null;
    CREATED_AT: string | Date;
}

export interface OrderTransportListItem {
    ID: number;
    STATUS: TransportStatus | string;
    ROLE: TransportOrderRole;
    TRACKING_NUMBER?: string | null;
    SHIPPER?: string | null;
    SHIPPER_SERVICE_CODE?: string | null;
    ORDER_AFTER?: string | Date | null;
}

export interface TransportLinkedOrder {
    ID: number;
    ESHOP_ORDER_ID: string | null;
    ROLE: TransportOrderRole;
}

export type TransportDetailResponse = TransportWithPackagesInterface & {
    ORDERS?: TransportLinkedOrder[];
};

export interface TransportShipperResponse {
    success: boolean;
    kind?: 'order' | 'validation' | 'failure';
    transportId: number;
    shipper: string;
    eshopKey: string;
    trackingNumbers?: string[];
    returnTrackingNumbers?: string[];
    exchangeTrackingNumber?: string;
    labelType?: string;
    label?: string;
    returnLabel?: string;
    exchangeLabel?: string | null;
    printFiles?: boolean;
    recipientState?: string;
    shipperServiceCode?: string;
    shipperShipmentId?: string;
    rawData?: Record<string, unknown>;
    shipmentRequest?: Record<string, unknown>;
    error?: string;
    down?: boolean;
}

export interface TransportLogUpdate {
    created_at: Date;
    data: unknown;
    user: unknown;
    message: string;
    type: 'create' | 'edit' | string;
}

export interface TransportLogDocument {
    transportId: number;
    created_at?: Date;
    updates: TransportLogUpdate[];
}

export interface TransportShippingTrackingDocument {
    transportId: number;
    set_at?: Date;
    data: unknown;
}
